import React, { useState } from 'react';
import { FiX, FiCheckCircle, FiXCircle, FiUser, FiMail, FiCalendar, FiClock, FiDollarSign, FiCheck } from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { useModalStore } from '../../stores';
import { useAuthStore } from '../../stores';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SuccessDisplay } from '../Common/SuccessDisplay';
import { User } from '../../types';
import { showToast } from '../../utils/toast';

const AdminApprovalModal: React.FC = () => {
  const { isOpen, closeModal, selectedUser } = useModalStore();
  const { user: currentUser } = useAuthStore();
  const [tradingTypes, setTradingTypes] = useState<Record<string, 'swing' | 'day' | ''>>({});
  
  // Only fetch pending masters when modal is open and user is admin
  const { data: pendingMasters, isLoading, refetch } = useApi.usePendingMasters({
    enabled: isOpen && currentUser?.role === 'admin',
  });
  
  const approveMutation = useApi.useApproveMaster({
    onSuccess: () => {
      refetch();
      // Reset trading type for the approved user
      if (selectedUser) {
        const userId = selectedUser._id || selectedUser.id;
        setTradingTypes(prev => {
          const updated = { ...prev };
          delete updated[userId!];
          return updated;
        });
      }
      if (selectedUser && pendingMasters?.users.length === 1) {
        closeModal('admin-approval');
      }
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to approve master');
    },
  });

  const rejectMutation = useApi.useRejectMaster({
    onSuccess: () => {
      refetch();
      if (selectedUser && pendingMasters?.users.length === 1) {
        closeModal('admin-approval');
      }
    },
  });

  if (!isOpen) return null;

  const userToShow = selectedUser || (pendingMasters?.users && pendingMasters.users[0]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 min-w-0">Pending Master Approvals</h2>
          <button
            onClick={() => closeModal('admin-approval')}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {isLoading ? (
            <Loader message="Loading pending masters..." />
          ) : !pendingMasters?.users || pendingMasters.users.length === 0 ? (
            <div className="text-center py-12">
              <FiCheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No pending master approvals</p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingMasters.users.map((user: User) => {
                const userId = user._id || user.id;
                return (
                  <div key={userId} className="border border-gray-200 rounded-lg p-4 sm:p-6">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className="bg-primary-100 p-3 rounded-full shrink-0">
                          <FiUser className="h-8 w-8 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-anywhere">{user.name}</h3>
                          <div className="flex items-center gap-2 mt-1 min-w-0">
                            <FiMail className="h-4 w-4 text-gray-400 shrink-0" />
                            <span className="text-gray-600 break-anywhere min-w-0">{user.email}</span>
                          </div>
                          {user.createdAt && (
                            <div className="flex items-center gap-2 mt-1">
                              <FiCalendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-500">
                                Registered: {new Date(user.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold shrink-0">
                        Pending
                      </span>
                    </div>

                    {/* Trading Type Selection */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select Trading Type <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Swing Trading */}
                        <button
                          type="button"
                          onClick={() => setTradingTypes({ ...tradingTypes, [userId!]: 'swing' })}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            tradingTypes[userId!] === 'swing'
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-300 hover:border-purple-400 bg-white'
                          }`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <FiCalendar className={`h-5 w-5 mt-0.5 ${tradingTypes[userId!] === 'swing' ? 'text-purple-600' : 'text-gray-400'}`} />
                            <div>
                              <h4 className={`font-bold ${tradingTypes[userId!] === 'swing' ? 'text-purple-600' : 'text-gray-900'}`}>
                                Swing Trading
                              </h4>
                              <p className="text-xs text-gray-500 mt-1">Long term trades</p>
                            </div>
                          </div>
                          <div className="space-y-1 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <FiDollarSign className="h-3 w-3" />
                              <span>Min: <strong>$20,000</strong></span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiClock className="h-3 w-3" />
                              <span>Days to weeks</span>
                            </div>
                          </div>
                          {tradingTypes[userId!] === 'swing' && (
                            <div className="mt-2 flex items-center gap-1 text-purple-600 font-medium text-sm">
                              <FiCheck className="h-4 w-4" />
                              <span>Selected</span>
                            </div>
                          )}
                        </button>

                        {/* Day Trading */}
                        <button
                          type="button"
                          onClick={() => setTradingTypes({ ...tradingTypes, [userId!]: 'day' })}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            tradingTypes[userId!] === 'day'
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-400 bg-white'
                          }`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <FiClock className={`h-5 w-5 mt-0.5 ${tradingTypes[userId!] === 'day' ? 'text-blue-600' : 'text-gray-400'}`} />
                            <div>
                              <h4 className={`font-bold ${tradingTypes[userId!] === 'day' ? 'text-blue-600' : 'text-gray-900'}`}>
                                Day Trading / Hedge Trading
                              </h4>
                              <p className="text-xs text-gray-500 mt-1">Short term trades</p>
                            </div>
                          </div>
                          <div className="space-y-1 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <FiDollarSign className="h-3 w-3" />
                              <span>Min: <strong>$1k-$2k</strong></span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiClock className="h-3 w-3" />
                              <span>Few hours max</span>
                            </div>
                          </div>
                          {tradingTypes[userId!] === 'day' && (
                            <div className="mt-2 flex items-center gap-1 text-blue-600 font-medium text-sm">
                              <FiCheck className="h-4 w-4" />
                              <span>Selected</span>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => {
                          const tradingType = tradingTypes[userId!];
                          if (!tradingType || (tradingType !== 'swing' && tradingType !== 'day')) {
                            showToast('error', 'Please select a trading type (Swing or Day) before approving.');
                            return;
                          }
                          approveMutation.mutate({ userId: userId!, tradingType });
                        }}
                        disabled={approveMutation.isPending || rejectMutation.isPending || !tradingTypes[userId!]}
                        className="btn-primary flex items-center justify-center sm:justify-start gap-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {approveMutation.isPending && approveMutation.variables?.userId === userId ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                          <span>Approving...</span>
                        </>
                      ) : (
                        <>
                          <FiCheckCircle className="h-5 w-5" />
                          <span>Approve</span>
                        </>
                      )}
                      </button>
                      <button
                        onClick={() => {
                          rejectMutation.mutate(userId!);
                          // Reset trading type when rejecting
                          setTradingTypes(prev => {
                            const updated = { ...prev };
                            delete updated[userId!];
                            return updated;
                          });
                        }}
                        disabled={approveMutation.isPending || rejectMutation.isPending}
                        className="btn-danger flex items-center justify-center sm:justify-start gap-2 flex-1"
                      >
                        {rejectMutation.isPending && rejectMutation.variables === userId ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            <span>Rejecting...</span>
                          </>
                        ) : (
                          <>
                            <FiXCircle className="h-5 w-5" />
                            <span>Reject</span>
                          </>
                        )}
                      </button>
                    </div>

                    {approveMutation.isSuccess && approveMutation.variables?.userId === userId && (
                      <SuccessDisplay message="Master trader approved successfully!" className="mt-4" />
                    )}
                    {rejectMutation.isSuccess && rejectMutation.variables === userId && (
                      <SuccessDisplay message="Master trader rejected successfully!" className="mt-4" />
                    )}
                    {approveMutation.isError && approveMutation.variables?.userId === userId && (
                      <ErrorDisplay error={approveMutation.error as any} className="mt-4" />
                    )}
                    {rejectMutation.isError && rejectMutation.variables === userId && (
                      <ErrorDisplay error={rejectMutation.error as any} className="mt-4" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminApprovalModal;

